import React, { useState, useEffect, useCallback } from "react";
import { Col, Row, Button, Table, Spinner, Badge, Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserPlus, faEllipsisV, faTrash, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";
import PopupModal from "../Common/Popup";
import WorkspaceMemberForm from "./WorkspaceMemberForm";
import { useAuth } from "../../contexts/AuthContext";

const WorkspaceDetailBlock = ({ workspaceId }) => {
  const { currentUser } = useAuth();
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [memberPopupShow, setMemberPopupShow] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchWorkspace = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Utils._apiRequest({
        path: Enums.endpoints.workspace.DETAIL.replace("[id]", workspaceId),
        type: "GET",
      });

      if (response && response.data) {
        setWorkspace(response.data);
      } else {
        toast.error("Failed to load workspace details.");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred while fetching workspace details.");
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (workspaceId) {
      fetchWorkspace();
    }
  }, [workspaceId, fetchWorkspace]);

  const handleChangeRole = async (email, currentRole, newRole) => {
    if (currentRole === newRole) return;

    console.log(email, currentRole, newRole);

    setActionLoading(email);
    try {
      const response = await Utils._apiRequest({
        path: Enums.endpoints.workspace.MEMBER,
        type: "PUT",
        payload: {
          workspaceId,
          email,
          role: newRole,
        },
      });

      if (response) {
        toast.success("Member role updated successfully.");
        fetchWorkspace();
      }
    } catch (error) {
      toast.error(error.message || "Failed to update member role.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveMember = async (userId, email) => {
    if (!userId) {
      toast.error("Missing userId. Cannot remove member.");
      return;
    }

    if (!window.confirm(`Are you sure you want to remove ${email} from this workspace?`)) {
      return;
    }

    setActionLoading(userId);
    try {
      const response = await Utils._apiRequest({
        path: Enums.endpoints.workspace.MEMBER,
        type: "DELETE",
        payload: {
          workspaceId,
          userId,
        },
      });

      if (response) {
        toast.success("Member removed successfully.");
        fetchWorkspace();
      }
    } catch (error) {
      toast.error(error.message || "Failed to remove member.");
    } finally {
      setActionLoading(null);
    }
  };

  if (loading && !workspace) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted">Loading workspace details...</p>
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="text-center my-5">
        <p className="text-muted">Workspace not found.</p>
        <Link to="/workspace">
          <Button variant="primary">Back to Workspaces</Button>
        </Link>
      </div>
    );
  }

  // Check if current user is owner or admin to show management actions
  const isOwner = workspace.owner && (workspace.owner._id === currentUser?._id || workspace.owner === currentUser?._id);

  // Find current user's role in this workspace
  let currentUserRole = "member";
  if (isOwner) {
    currentUserRole = "owner";
  } else if (workspace.members) {
    const memberRecord = workspace.members.find(m => m.user?._id === currentUser?._id || m.user?.email === currentUser?.email);
    if (memberRecord) {
      currentUserRole = memberRecord.role;
    }
  }

  const canManageMembers = currentUserRole === "owner" || currentUserRole === "admin";

  return (
    <>
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <Link to="/workspace">
          <Button variant="outline-secondary" size="sm">
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to List
          </Button>
        </Link>
        {canManageMembers && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setMemberPopupShow(true)}
          >
            <FontAwesomeIcon className="me-2" icon={faUserPlus} />
            Add Member
          </Button>
        )}
      </div>

      <Row>
        <Col md={4} className="mb-4">
          <div className="p-3 bg-body rounded shadow-sm feature-box h-100">
            <h6 className="heading border-bottom pb-2 mb-3">Workspace Info</h6>
            <div className="mb-3">
              <strong>Name:</strong>
              <div>{workspace.name}</div>
            </div>
            <div className="mb-3">
              <strong>Description:</strong>
              <div className="text-muted">{workspace.description || "No description provided."}</div>
            </div>
            <div className="mb-3">
              <strong>Owner:</strong>
              <div>
                {workspace.owner?.fullName || workspace.owner?.email || "Unknown"}
              </div>
            </div>
            <div className="mb-3">
              <strong>Created At:</strong>
              <div>
                {workspace.createdAt ? Utils.dateFormat(workspace.createdAt, true) : "-"}
              </div>
            </div>
          </div>
        </Col>

        <Col md={8} className="mb-4">
          <div className="p-3 bg-body rounded shadow-sm feature-box h-100">
            <h6 className="heading border-bottom pb-2 mb-3">
              Members ({workspace.members?.length || 0})
            </h6>

            <Table responsive hover className="align-middle">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  {canManageMembers && <th className="text-end">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {workspace.members && workspace.members.length > 0 ? (
                  workspace.members.map((member, idx) => {

                    const userObj = member.user;
                    const email = userObj?.email;
                    const displayName = userObj?.fullName || email || "Unknown User";
                    const initial = displayName.charAt(0).toUpperCase();
                    const bgColor = Utils.stringToColor(displayName);
                    const userId = userObj?._id;
                    const isProcessing = actionLoading === userId;

                    return (
                      <tr key={idx}>
                        <td>
                          <div className="d-flex align-items-center">
                            <div
                              className="rounded-circle text-white d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                              style={{
                                width: "36px",
                                height: "36px",
                                backgroundColor: bgColor,
                                fontSize: "16px",
                                fontWeight: "bold",
                              }}
                            >
                              {initial}
                            </div>
                            <div>
                              <div className="fw-bold">{displayName}</div>
                              <div className="text-muted small">{email}</div>
                              {member.status === "pending" && (
                                <Badge bg="warning" text="dark" className="mt-1">Pending</Badge>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          {member.role === "admin" ? (
                            <Badge bg="primary">Admin</Badge>
                          ) : (
                            <Badge bg="secondary">Member</Badge>
                          )}
                        </td>
                        {canManageMembers && (
                          <td className="text-end">
                            {isProcessing ? (
                              <Spinner animation="border" size="sm" variant="secondary" />
                            ) : (
                              <Dropdown align="end">
                                <Dropdown.Toggle variant="link" className="text-secondary p-0 shadow-none border-0" id={`dropdown-member-${idx}`}>
                                  <FontAwesomeIcon icon={faEllipsisV} fixedWidth />
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                  <Dropdown.Header>Change Role</Dropdown.Header>
                                  <Dropdown.Item
                                    active={member.role === "member"}
                                    onClick={() => handleChangeRole(email, member.role, "member")}
                                  >
                                    Set as Member
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    active={member.role === "admin"}
                                    onClick={() => handleChangeRole(email, member.role, "admin")}
                                  >
                                    Set as Admin
                                  </Dropdown.Item>
                                  <Dropdown.Divider />
                                  <Dropdown.Item
                                    className="text-danger"
                                    onClick={() => handleRemoveMember(userId, email)}
                                  >
                                    <FontAwesomeIcon icon={faTrash} className="me-2" />
                                    Remove from Workspace
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={canManageMembers ? 3 : 2} className="text-center text-muted py-3">
                      No members found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Col>
      </Row>

      <PopupModal
        title={workspace ? `Add Member to ${workspace.name}` : "Add Member"}
        popupShow={memberPopupShow}
        setPopupShow={setMemberPopupShow}
      >
        {workspace && (
          <WorkspaceMemberForm
            workspaceId={workspace._id}
            setPopupShow={(show) => {
              setMemberPopupShow(show);
              if (!show) fetchWorkspace(); // Refresh after adding or closing
            }}
          />
        )}
      </PopupModal>
    </>
  );
};

export default WorkspaceDetailBlock;
